/** Model helpers for the Transaction Wizard designer (obj_type "U").

    The model's TYPES are the shared contract
    (`@twasta/page-runtime/wizard/contract`) — the runner reads exactly what this
    designer writes, so there is no second copy here. This file holds what only
    the designer needs: a blank model, the step-kind vocabulary with its labels,
    a blank step per kind, and the edges the flow graph draws. */

import type {
  WizardCallOutcome, WizardModel, WizardNext, WizardOp, WizardOpKind, WizardStep,
  WizardStepKind, WizardTool, WizardToolKind,
} from '@twasta/page-runtime/wizard/contract'

export type {
  WizardCallBlock, WizardCallOutcome, WizardContextPanel, WizardFetchRequest,
  WizardInput, WizardModel, WizardNext, WizardOp, WizardOpKind,
  WizardReportBlock, WizardReturnRoute, WizardScanParse, WizardStep,
  WizardStepKind, WizardTool, WizardToolKind, WizardVar,
} from '@twasta/page-runtime/wizard/contract'

// Literal copies of the contract's vocabulary lists: they are runtime values
// there, and reading them from the runner's module would load it into the
// designer just for three arrays.
export const STEP_KINDS: WizardStepKind[] = [
  'info', 'select', 'input', 'scan', 'grid', 'confirm', 'fetch_url', 'auto',
  'branch', 'load', 'repeat', 'save', 'show', 'print', 'action', 'call', 'handoff',
  'function', 'redirect',
]

export const OP_KINDS: WizardOpKind[] = [
  'set', 'item_change', 'add_line', 'complete_line', 'delete_line', 'match_line',
  'lookup', 'fetch_lines', 'recalc_lines', 'run_action', 'validate', 'fetch_url',
  'goto', 'message',
]

export const TOOL_KINDS: WizardToolKind[] = ['open', 'assistant', 'print', 'action', 'goto', 'call']

export const OUTCOMES: WizardCallOutcome[] = [
  'completed', 'cancelled', 'pending_approval', 'pending_assignee', 'failed',
]

export interface KindMeta {
  label: string
  /** One line, said the way an author would ask for it. */
  hint: string
  /** Material icon name. */
  icon: string
  /** The step shows a screen of its own (false: it runs and moves on). */
  screen: boolean
}

export const KIND_META: Record<WizardStepKind, KindMeta> = {
  info: { label: 'Info', hint: 'Instructions or a caution, optionally acknowledged.', icon: 'info', screen: true },
  select: { label: 'Select', hint: 'Choose from a list of values.', icon: 'checklist', screen: true },
  input: { label: 'Input', hint: 'Type one or more fields.', icon: 'edit_note', screen: true },
  scan: { label: 'Scan', hint: 'Scan a barcode / QR, parse it and fill columns.', icon: 'qr_code_scanner', screen: true },
  grid: { label: 'Grid', hint: 'Review or edit many lines at once.', icon: 'table_rows', screen: true },
  confirm: { label: 'Confirm', hint: 'Show the record and check it before saving.', icon: 'fact_check', screen: true },
  fetch_url: { label: 'Read from URL', hint: 'Read values from a connected service (server-side).', icon: 'cloud_download', screen: false },
  auto: { label: 'Automatic', hint: 'Run operations with no screen, then move on.', icon: 'bolt', screen: false },
  branch: { label: 'Branch', hint: 'Route on a condition — no screen.', icon: 'call_split', screen: false },
  load: { label: 'Load record', hint: 'Find a record, then edit it or add a new one.', icon: 'manage_search', screen: false },
  repeat: { label: 'Repeat per line', hint: 'Run a run of steps once per matching line.', icon: 'repeat', screen: false },
  save: { label: 'Save', hint: 'Save the record (validations and approval apply).', icon: 'save', screen: false },
  show: { label: 'Show', hint: 'A visual / dashboard / report IS the step.', icon: 'insights', screen: true },
  print: { label: 'Print', hint: 'Print a report, and/or file it in a document repository (saved data only).', icon: 'print', screen: false },
  sign: { label: 'Sign', hint: 'Sign the report a print step filed (the repository\'s own signatures).', icon: 'draw', screen: true },
  send: { label: 'Send', hint: "Send the base transaction's message (a send button) for the saved record.", icon: 'send', screen: false },
  action: { label: 'Action', hint: "Run one of the base transaction's action buttons.", icon: 'smart_button', screen: false },
  call: { label: 'Call wizard', hint: 'Pause, run another wizard, come back with its result.', icon: 'subdirectory_arrow_right', screen: false },
  handoff: { label: 'Hand off', hint: 'End this wizard and start another (no return).', icon: 'forward', screen: false },
  function: { label: 'Server function', hint: 'Run server code (an outside API, with a stored credential) and map its answer.', icon: 'terminal', screen: false },
  redirect: { label: 'Other website', hint: 'Send the user to another website and continue when it posts back.', icon: 'open_in_new', screen: true },
}

export const OP_LABEL: Record<WizardOpKind, string> = {
  set: 'Set a value',
  item_change: 'Re-run item change',
  add_line: 'Add a line',
  complete_line: 'Complete the line',
  delete_line: 'Delete the line',
  match_line: 'Match a line',
  lookup: 'Look up',
  fetch_lines: 'Fetch lines (Line Entry)',
  recalc_lines: 'Recalculate lines',
  run_action: 'Run an action',
  validate: 'Validate',
  fetch_url: 'Read from URL',
  goto: 'Go to step',
  message: 'Show a message',
}

export const TOOL_LABEL: Record<WizardToolKind, string> = {
  open: 'Open an object',
  assistant: 'Ask the Assistant',
  print: 'Print a report',
  action: 'Run an action',
  goto: 'Go to step',
  call: 'Call a wizard',
}

export function blankWizard(objName: string): WizardModel {
  return {
    obj_name: objName,
    title: '',
    description: '',
    base_object: '',
    line_form: '2',
    mode: 'add',
    inputs: [],
    outputs: { txn_id: '{run.txn_id}' },
    vars: [],
    start: 'start',
    steps: [
      { id: 'start', kind: 'input', title: 'Enter the details', fields: [], next: 'save' },
      { id: 'save', kind: 'save', warnings: 'ask', back: false, next: { finish: true } },
    ],
    tools: [],
    finish: { then: 'restart' },
    suspend: 'memory',
  }
}

/** Accept the file shape (`{wizard: {...}}`) or the inner model; fill what the
 *  editors assume is present. Never drops keys it does not know. */
export function sanitizeWizard(raw: unknown, objName: string): WizardModel {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const inner = (r.wizard && typeof r.wizard === 'object' ? r.wizard : r) as Record<string, unknown>
  if (!Object.keys(inner).length) return blankWizard(objName)
  const m = { ...inner } as unknown as WizardModel
  m.obj_name = String(m.obj_name || objName)
  m.base_object = String(m.base_object || '')
  m.mode = (['add', 'edit', 'add_or_edit', 'fill'] as const).includes(m.mode) ? m.mode : 'add'
  m.steps = Array.isArray(m.steps) ? m.steps.filter((s) => s && typeof s === 'object') : []
  m.start = String(m.start || m.steps[0]?.id || '')
  // The server stamps this; it must never be what makes a model "dirty".
  delete m.fingerprint
  return m
}

/** A fresh step id of the form `<kind>_<n>` not already taken. */
export function newStepId(model: WizardModel, kind: string): string {
  const taken = new Set(model.steps.map((s) => s.id))
  for (let n = 1; ; n++) {
    const id = `${kind}_${n}`
    if (!taken.has(id)) return id
  }
}

export function blankStep(kind: WizardStepKind, id: string): WizardStep {
  const base: WizardStep = { id, kind, title: KIND_META[kind].label }
  switch (kind) {
    case 'info': return { ...base, body: '', acknowledge: false }
    case 'select': return { ...base, target: '', source: { column: '' }, display: 'list', auto_pick_single: true }
    case 'input': return { ...base, fields: [], layout: 'stack' }
    case 'scan': return { ...base, prompt: 'Scan the barcode', parse: { format: 'raw' }, map: {}, manual_entry: true }
    case 'grid': return { ...base, columns: [], editable: [], allow_add: false, allow_delete: false }
    case 'confirm': return { ...base, show: [], validate: true }
    case 'fetch_url': return { ...base, request: { method: 'GET', path: '' }, map: {}, timeout_ms: 5000 }
    case 'auto': return { ...base, title: undefined, do: [] }
    case 'branch': return { ...base, title: undefined, cases: [], else: '' }
    case 'load': return { ...base, match: {}, on_found: { mode: 'edit' }, on_missing: { mode: 'add' }, on_many: 'pick' }
    case 'repeat': return { ...base, over: '', order_by: [], until_step: '' }
    case 'save': return { ...base, title: undefined, warnings: 'ask', back: false }
    case 'show': return { ...base, content: { kind: 'visual', obj_name: '', params: {} }, acknowledge: false }
    case 'print': return { ...base, report: { obj_name: '', param_map: {} }, delivery: 'print_dialog' }
    case 'sign': return { ...base, document: '' }
    case 'send': return { ...base, action: '' }
    case 'action': return { ...base, action: '' }
    case 'call': return { ...base, wizard: '', with: {}, returns: {}, on_return: { completed: { resume: 'same' } }, run_as: 'self' }
    case 'handoff': return { ...base, wizard: '', with: {}, require_saved: true }
    case 'function': return { ...base, function: { name: '', input: {} }, map: {} }
    case 'redirect': return { ...base, redirect: { prepare: { name: '', input: {} }, receive: { name: '', input: {} }, timeout_min: 30 }, map: {} }
    default: return base
  }
}

export function blankOp(op: WizardOpKind): WizardOp {
  switch (op) {
    case 'set': return { op, target: '', value: '' }
    case 'item_change': return { op, field: '' }
    case 'add_line': return { op, set: {}, complete: true }
    case 'match_line': return { op, on: [], on_none: 'add_line' }
    case 'lookup': return { op, lookup: '', filter: '', map: {} }
    case 'fetch_lines': return { op, source: '', select: 'all' }
    case 'run_action': return { op, action: '' }
    case 'validate': return { op, scope: 'tree' }
    case 'goto': return { op, goto: '' }
    case 'message': return { op, message: '', kind: 'info' }
    default: return { op }
  }
}

export function blankTool(kind: WizardToolKind): WizardTool {
  switch (kind) {
    case 'open': return { label: 'Open', kind, open: { obj_type: 'V', obj_name: '', params: {}, presentation: 'drawer' } }
    case 'assistant': return { label: 'Ask', kind, assistant: { mode: 'intent', intent_id: '', param_map: {} } }
    case 'print': return { label: 'Print', kind, report: { obj_name: '', param_map: {} }, delivery: 'print_dialog' }
    case 'action': return { label: 'Run', kind, action: '' }
    case 'goto': return { label: 'Go to', kind, goto: '' }
    case 'call': return { label: 'Call', kind, call: { wizard: '', with: {} } }
    default: return { label: 'Tool', kind }
  }
}

// ── Flow edges ───────────────────────────────────────────────────────────

export type EdgeKind = 'next' | 'case' | 'else' | 'goto' | 'return' | 'issue' | 'body'

export interface FlowEdge {
  from: string
  to: string
  kind: EdgeKind
  label?: string
}

/** `goto:<step>` strings (on_none, on_error, pick_row.then …) → the step id. */
export function gotoTarget(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const m = /^goto:(.+)$/.exec(v.trim())
  return m ? m[1].trim() : null
}

function nextEdges(from: string, next: WizardNext | undefined, out: FlowEdge[]) {
  if (!next) return
  if (typeof next === 'string') { out.push({ from, to: next, kind: 'next' }); return }
  if (Array.isArray(next)) {
    for (const c of next) {
      if ('goto' in c && c.goto) {
        out.push({ from, to: c.goto, kind: c.when ? 'case' : 'next', label: c.when || undefined })
      } else if ('finish' in c) {
        out.push({ from, to: '__finish', kind: c.when ? 'case' : 'next', label: c.when || undefined })
      }
    }
    return
  }
  if (typeof next === 'object' && 'finish' in next) out.push({ from, to: '__finish', kind: 'next' })
}

function opsEdges(from: string, ops: WizardOp[] | undefined, out: FlowEdge[]) {
  for (const op of ops || []) {
    if (op.op === 'goto' && op.goto) out.push({ from, to: op.goto, kind: 'goto' })
    for (const key of ['on_none', 'on_many'] as const) {
      const t = gotoTarget(op[key])
      if (t) out.push({ from, to: t, kind: 'goto', label: key })
    }
  }
}

/** Every route out of every step — what the read-only flow graph draws. */
export function flowEdges(model: WizardModel): FlowEdge[] {
  const out: FlowEdge[] = []
  for (const s of model.steps) {
    nextEdges(s.id, s.next, out)
    for (const c of s.cases || []) if (c.goto) out.push({ from: s.id, to: c.goto, kind: 'case', label: c.when })
    if (s.else) out.push({ from: s.id, to: s.else, kind: 'else', label: 'else' })
    if (s.body) out.push({ from: s.id, to: s.body, kind: 'body', label: 'per line' })
    if (s.until_step) out.push({ from: s.id, to: s.until_step, kind: 'body', label: 'until' })
    if (s.on_saved) out.push({ from: s.id, to: s.on_saved, kind: 'next', label: 'saved' })
    if (s.on_pending_approval) out.push({ from: s.id, to: s.on_pending_approval, kind: 'case', label: 'pending approval' })
    const errTo = gotoTarget(s.on_error)
    if (errTo) out.push({ from: s.id, to: errTo, kind: 'issue', label: 'error' })
    if (s.on_found?.goto) out.push({ from: s.id, to: s.on_found.goto, kind: 'case', label: 'found' })
    const miss = s.on_missing
    if (miss && typeof miss === 'object' && miss.goto) out.push({ from: s.id, to: miss.goto, kind: 'case', label: 'missing' })
    const missTo = gotoTarget(miss)
    if (missTo) out.push({ from: s.id, to: missTo, kind: 'case', label: 'missing' })
    for (const iss of s.on_issue || []) if (iss.goto) out.push({ from: s.id, to: iss.goto, kind: 'issue', label: iss.code })
    for (const [outcome, route] of Object.entries(s.on_return || {})) {
      if (!route) continue
      const to = route.goto || (route.resume && !['same', 'next'].includes(route.resume) ? route.resume : null)
      if (to) out.push({ from: s.id, to, kind: 'return', label: outcome })
    }
    for (const t of s.tools || []) if (t.kind === 'goto' && t.goto) out.push({ from: s.id, to: t.goto, kind: 'goto', label: t.label })
    for (const t of s.row_tools || []) if (t.kind === 'goto' && t.goto) out.push({ from: s.id, to: t.goto, kind: 'goto', label: t.label })
    for (const p of [...(s.context || []), ...(s.content ? [s.content] : [])]) {
      const t = gotoTarget(p.pick_row?.then)
      if (t) out.push({ from: s.id, to: t, kind: 'goto', label: 'pick row' })
    }
    opsEdges(s.id, s.then, out)
    opsEdges(s.id, s.do, out)
  }
  return out
}

/** The step ids a field can jump to, for the goto pickers. */
export function stepIds(model: WizardModel): string[] {
  return model.steps.map((s) => s.id)
}

/** Rename a step everywhere the model names it: `start`, `safe_resume`, every
 *  route key and every `goto:<id>` string. Keys that hold text (titles,
 *  messages, expressions) are left alone — a step id inside a template is
 *  `{step.ID.value}` and is rewritten as such. */
export function renameStep(model: WizardModel, from: string, to: string): WizardModel {
  if (from === to) return model
  const ROUTE_KEYS = new Set(['goto', 'next', 'else', 'body', 'until_step', 'on_saved',
    'on_pending_approval', 'resume', 'start', 'safe_resume', 'step'])
  const tokenRe = new RegExp(`\\{step\\.${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.`, 'g')
  const walk = (v: unknown, key: string | null): unknown => {
    if (typeof v === 'string') {
      if (key && ROUTE_KEYS.has(key) && v === from) return to
      if (v === `goto:${from}`) return `goto:${to}`
      return v.includes(`{step.${from}.`) ? v.replace(tokenRe, `{step.${to}.`) : v
    }
    if (Array.isArray(v)) return v.map((x) => walk(x, key === 'next' ? 'next' : null))
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
        out[k] = k === 'id' ? x : walk(x, k)
      }
      return out
    }
    return v
  }
  const next = walk(model, null) as WizardModel
  next.steps = next.steps.map((s) => (s.id === from ? { ...s, id: to } : s))
  return next
}
